import { Control, useFormState, UseFormWatch, useWatch } from 'react-hook-form';
import { TextKey } from '@mews-commander/enums';
import { DateIntervalFields } from '@mews-commander/form';
import { lt, FormSelectField, useTodayAsStructuredDate, showSuccessAlert } from '@mews/framework';
import { Stack, DropdownItem, Button } from '@optimus-web/core';
import { useRepositoryMutation } from '@mews-commander/data-access';
import { useGetSwedishCashRegister } from './useGetSwedishCashRegister';
import { FilterFormKeys, FilterFormValues } from './filterSchema';
import { useHasSwedishIntegrationEnabled } from './useHasSwedishIntegrationEnabled';

const CashRegisters: React.FC = () => {
    const integrationId = useWatch<FilterFormValues>({
        name: FilterFormKeys.swedishIntegrationId,
    });
    const { data: cashRegisterOptions, isLoading } = useGetSwedishCashRegister({
        integrationId,
    });

    return (
        <FormSelectField
            name={FilterFormKeys.cashRegisterId}
            label={lt(TextKey.SwedishCashRegister)}
            loader={isLoading}
            required
        >
            {cashRegisterOptions?.map((option) => (
                <DropdownItem key={option.id} value={option.id} label={option.name} />
            ))}
        </FormSelectField>
    );
};

const FiscalRegistryIntegration: React.FC = () => {
    const { data } = useHasSwedishIntegrationEnabled();
    return (
        <FormSelectField
            name={FilterFormKeys.swedishIntegrationId}
            label={lt(TextKey.FiscalRegistryIntegration)}
            required
        >
            {data?.map((integration) => (
                <DropdownItem key={integration.id} value={integration.id} label={integration.name} />
            ))}
        </FormSelectField>
    );
};

const Fields: React.FC = () => {
    const today = useTodayAsStructuredDate();

    return (
        <Stack vertical spacing="200">
            <DateIntervalFields
                mode="iso"
                maxDate={today}
                startDateProps={{ name: FilterFormKeys.dateFrom, label: lt(TextKey.Start), required: true }}
                endDateProps={{ name: FilterFormKeys.dateTo, label: lt(TextKey.End), required: true }}
            />
            <FiscalRegistryIntegration />
            <CashRegisters />
        </Stack>
    );
};

const Action: React.FC<{ watch: UseFormWatch<FilterFormValues>; control: Control<FilterFormValues> }> = ({
    watch,
    control,
}) => {
    const { dateFrom, dateTo, swedishIntegrationId, cashRegisterId } = watch();
    const { isValid } = useFormState<FilterFormValues>({
        control,
    });

    const { mutate, isPending } = useRepositoryMutation('swedishCashRegisterFiscalDay', 'exportEJournal', {
        onSuccess: () => {
            showSuccessAlert({
                description: lt(TextKey.ExportQueuedSuccessMessage),
            });
        },
    });
    return (
        <Button
            type="button"
            variant="secondary"
            loading={isPending}
            disabled={!isValid}
            onClick={() => {
                if (!isValid) {
                    return;
                }
                mutate({
                    date: {
                        start: dateFrom,
                        end: dateTo,
                    },
                    integrationId: swedishIntegrationId,
                    swedishCashRegisterId: cashRegisterId,
                });
            }}
        >
            {lt(TextKey.SwedishGenerateEJournalButtonText)}
        </Button>
    );
};

export const filterSidebar = {
    title: () => lt(TextKey.Filter),
    fields: <Fields />,
    actions: Action,
};
